'use client';
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { useRouter } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ShowroomSection from '@/components/ShowroomSection';
import ProductSpecsHoverCard from '@/components/ProductSpecsHoverCard';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { saveCheckoutSelection } from '@/lib/checkout-selection';
import type { HomepageShowroomItem } from '@/lib/homepage-config';
import type { ProductDetail as Product, ProductReview } from '@/lib/products/product-detail';
import { buildProductCardSpecBadges } from '@/lib/product-spec-badges';
import { normalizeProductImageList, productImageOrFallback } from '@/lib/products/product-image';

interface ProductDetailClientProps {
  slug: string;
  initialProduct: Product;
  initialReviews: ProductReview[];
  showroomVisible?: boolean;
  showroomTitle?: string;
  showroomItems?: HomepageShowroomItem[];
}

interface RelatedProductItem {
  id: string;
  name: string;
  slug: string;
  price: number;
  original_price: number;
  discount: number;
  image: string;
  rating: number;
  review_count: number;
  stock: number;
  specs: string;
  full_specs: Record<string, string>;
}

function formatPrice(price: number): string {
  return new Intl.NumberFormat('vi-VN').format(price) + '₫';
}

function relatedDiscountPercent(product: RelatedProductItem): number {
  const price = Number(product.price || 0);
  const original = Number(product.original_price || 0);
  if (product.discount > 0) return Math.min(99, Math.round(product.discount));
  if (original > price && original > 0)
    return Math.min(99, Math.round((1 - price / original) * 100));
  return 0;
}

function isModifiedLinkClick(event: React.MouseEvent<HTMLAnchorElement>) {
  return (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.currentTarget.target === '_blank'
  );
}

function RelatedSpecIcon({ index }: { index: number }) {
  const paths = [
    'M8 1.5 2.5 4.5v7L8 14.5l5.5-3v-7L8 1.5Zm0 2.2 3.5 1.9L8 7.5 4.5 5.6 8 3.7Zm-3.8 3 3 1.6v3.4l-3-1.6V6.7Zm7.6 0v3.4l-3 1.6V8.3l3-1.6Z',
    'M3 2h10a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H9.5L8 14l-1.5-2H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Zm1 3v1h8V5H4Zm0 3v1h5V8H4Z',
    'M2 3h12v8H9.5v2H12v1H4v-1h2.5v-2H2V3Zm1.5 1.5v5h9v-5h-9Z',
    'M4 2h8v12H4V2Zm1.5 1.5v9h5v-9h-5ZM7 11h2v1H7v-1Z',
    'M2 4.5A2.5 2.5 0 0 1 4.5 2h7A2.5 2.5 0 0 1 14 4.5v7a2.5 2.5 0 0 1-2.5 2.5h-7A2.5 2.5 0 0 1 2 11.5v-7Zm2.5-1A1 1 0 0 0 3.5 4.5v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1v-7a1 1 0 0 0-1-1h-7ZM5 5h6v1H5V5Zm0 2.5h6v1H5v-1Zm0 2.5h4v1H5v-1Z',
    'M8 1.5A6.5 6.5 0 1 1 8 14.5 6.5 6.5 0 0 1 8 1.5Zm0 2A4.5 4.5 0 1 0 8 12.5 4.5 4.5 0 0 0 8 3.5Zm-.5 1.5h1v3.2l2.2 1.3-.5.9-2.7-1.6V5Z',
  ];

  return (
    <svg
      viewBox="0 0 16 16"
      className="h-3.5 w-3.5 shrink-0 text-slate-500"
      fill="currentColor"
      aria-hidden
    >
      <path d={paths[index % paths.length]} />
    </svg>
  );
}

function RelatedCartIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3.5 4.5h2.2l2.1 10.2a2 2 0 0 0 2 1.6h7.5a2 2 0 0 0 1.9-1.4l1.3-5.2H7" />
      <path d="M8.4 9.7h11.7" />
      <path d="M9.3 13h9.9" />
      <path d="M11.5 9.7v6.6" />
      <path d="M16.1 9.7v6.6" />
      <circle cx="10.2" cy="20" r="1.35" fill="currentColor" stroke="none" />
      <circle cx="18" cy="20" r="1.35" fill="currentColor" stroke="none" />
    </svg>
  );
}

function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'lg' }) {
  const starSize = size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`${starSize} ${star <= Math.round(rating) ? 'text-yellow-400' : 'text-gray-300'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

function sanitizeProductHtml(html: string): string {
  if (!html) return '';
  if (typeof window === 'undefined') return html;

  const replacedBrandText = String(html)
    .replace(/tin\s*h[oọ]c\s*ng[oô]i\s*sao/gi, 'dntechstore')
    .replace(/ng[oô]i\s*sao\s*tin\s*h[oọ]c/gi, 'dntechstore')
    .replace(/tinhocngoisao\.com/gi, 'dntechstore.com');

  const template = document.createElement('template');
  template.innerHTML = replacedBrandText;

  const blockedTags = [
    'script',
    'style',
    'iframe',
    'object',
    'embed',
    'form',
    'input',
    'button',
    'textarea',
    'select',
    'meta',
    'link',
  ];

  blockedTags.forEach((tag) => {
    template.content.querySelectorAll(tag).forEach((node) => node.remove());
  });

  template.content.querySelectorAll('*').forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const attrName = attr.name.toLowerCase();
      const attrValue = attr.value ?? '';

      if (attrName.startsWith('on')) {
        el.removeAttribute(attr.name);
        return;
      }

      if ((attrName === 'href' || attrName === 'src') && /^\s*javascript:/i.test(attrValue)) {
        el.removeAttribute(attr.name);
      }
    });

    if (el.tagName.toLowerCase() === 'a') {
      const span = document.createElement('span');
      span.innerHTML = el.innerHTML;
      span.className = 'text-inherit no-underline';
      el.replaceWith(span);
    }
  });

  return template.innerHTML;
}

export default function ProductDetailClient({
  slug,
  initialProduct,
  initialReviews,
  showroomVisible = true,
  showroomTitle,
  showroomItems,
}: ProductDetailClientProps) {
  const router = useRouter();
  const [product, setProduct] = useState<Product | null>(initialProduct);
  const [reviews, setReviews] = useState<ProductReview[]>(initialReviews);
  const [loading, setLoading] = useState(!initialProduct);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [mainImageLoaded, setMainImageLoaded] = useState(false);
  const [zoomActive, setZoomActive] = useState(false);

  const zoomLensRef = useRef<HTMLDivElement>(null);
  const zoomPreviewRef = useRef<HTMLDivElement>(null);
  const zoomFrameRef = useRef<number | null>(null);

  const [quantity, setQuantity] = useState(1);
  const [cartMessage, setCartMessage] = useState('');
  const [specsExpanded, setSpecsExpanded] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    name: '',
    email: '',
    rating: 5,
    title: '',
    content: '',
  });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState(false);
  const { addToCart, items: cartItems } = useCart();
  const { user, loading: authLoading } = useAuth();
  const [relatedProducts, setRelatedProducts] = useState<RelatedProductItem[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [checkingPurchase, setCheckingPurchase] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const relatedDragRef = useRef({
    active: false,
    pointerId: -1,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    lastClientX: 0,
    lastTime: 0,
    velocity: 0,
    animationFrame: 0,
    moved: false,
    suppressClickUntil: 0,
  });

  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    let stopped = false;
    let frameId = 0;
    const timeouts: number[] = [];
    const startedAt = performance.now();

    const scrollToTop = () => {
      if (stopped) return;
      window.scrollTo(0, 0);
    };

    const holdTopDuringBrowserRestore = () => {
      scrollToTop();
      if (!stopped && performance.now() - startedAt < 700) {
        frameId = window.requestAnimationFrame(holdTopDuringBrowserRestore);
      }
    };

    const releaseOnUserIntent = () => {
      stopped = true;
      if (frameId) window.cancelAnimationFrame(frameId);
      timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };

    const handlePageShow = () => {
      scrollToTop();
      timeouts.push(window.setTimeout(scrollToTop, 50));
    };

    holdTopDuringBrowserRestore();
    [0, 80, 180, 360, 650].forEach((delay) => {
      timeouts.push(window.setTimeout(scrollToTop, delay));
    });

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('wheel', releaseOnUserIntent, { passive: true, once: true });
    window.addEventListener('touchstart', releaseOnUserIntent, { passive: true, once: true });
    window.addEventListener('keydown', releaseOnUserIntent, { once: true });

    return () => {
      releaseOnUserIntent();
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('wheel', releaseOnUserIntent);
      window.removeEventListener('touchstart', releaseOnUserIntent);
      window.removeEventListener('keydown', releaseOnUserIntent);
    };
  }, [slug]);

  const stopRelatedDragAnimation = () => {
    if (relatedDragRef.current.animationFrame) {
      cancelAnimationFrame(relatedDragRef.current.animationFrame);
      relatedDragRef.current.animationFrame = 0;
    }
  };

  const getRelatedScrollMetrics = () => {
    const el = scrollRef.current;
    if (!el) return null;
    const firstCard = el.querySelector<HTMLElement>('[data-related-card="1"]');
    const track = el.firstElementChild as HTMLElement | null;
    const gap = track
      ? parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '12')
      : 12;
    const cardWidth = firstCard?.offsetWidth || 188;
    const itemStep = cardWidth + gap;
    const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    return { el, itemStep, maxLeft };
  };

  const snapRelatedToNearest = () => {
    const metrics = getRelatedScrollMetrics();
    if (!metrics) return;
    const { el, itemStep, maxLeft } = metrics;
    const target = Math.min(maxLeft, Math.max(0, Math.round(el.scrollLeft / itemStep) * itemStep));
    if (Math.abs(el.scrollLeft - target) > 1) {
      el.scrollTo({ left: target, behavior: 'smooth' });
    }
  };

  const runRelatedInertia = () => {
    const metrics = getRelatedScrollMetrics();
    if (!metrics) return;
    const { el, maxLeft } = metrics;
    const drag = relatedDragRef.current;
    const now = performance.now();
    const elapsed = Math.min(now - drag.lastTime, 32);
    drag.lastTime = now;

    el.scrollLeft = Math.min(maxLeft, Math.max(0, el.scrollLeft - drag.velocity * elapsed));
    drag.velocity *= Math.pow(0.91, elapsed / 16);
    const reachedEdge = el.scrollLeft <= 0 || el.scrollLeft >= maxLeft - 1;

    if (Math.abs(drag.velocity) > 0.12 && !reachedEdge) {
      drag.animationFrame = requestAnimationFrame(runRelatedInertia);
    } else {
      drag.animationFrame = 0;
      el.style.scrollBehavior = '';
      el.style.cursor = '';
      el.style.userSelect = '';
      setIsDragging(false);
      snapRelatedToNearest();
    }
  };

  const startRelatedInertia = () => {
    stopRelatedDragAnimation();
    relatedDragRef.current.lastTime = performance.now();
    relatedDragRef.current.animationFrame = requestAnimationFrame(runRelatedInertia);
  };

  const handleRelatedPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!scrollRef.current || e.pointerType !== 'mouse' || e.button !== 0) return;
    stopRelatedDragAnimation();
    const now = performance.now();
    relatedDragRef.current = {
      active: true,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: scrollRef.current.scrollLeft,
      lastClientX: e.clientX,
      lastTime: now,
      velocity: 0,
      animationFrame: 0,
      moved: false,
      suppressClickUntil: relatedDragRef.current.suppressClickUntil,
    };
    scrollRef.current.style.scrollBehavior = 'auto';
    scrollRef.current.style.userSelect = 'none';
  };

  const handleRelatedPointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse') return;
    if (
      relatedDragRef.current.pointerId === e.pointerId &&
      e.currentTarget.hasPointerCapture(e.pointerId)
    ) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    const drag = relatedDragRef.current;
    const shouldSnap = drag.moved;
    drag.active = false;
    drag.suppressClickUntil = shouldSnap ? Date.now() + 900 : 0;
    if (scrollRef.current && shouldSnap && Math.abs(drag.velocity) > 0.08) {
      scrollRef.current.style.cursor = '';
      startRelatedInertia();
      return;
    }

    if (scrollRef.current) {
      scrollRef.current.style.scrollBehavior = '';
      scrollRef.current.style.userSelect = '';
      if (shouldSnap) snapRelatedToNearest();
      scrollRef.current.style.cursor = '';
    }
    setIsDragging(false);
  };

  const handleRelatedPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse') return;
    const drag = relatedDragRef.current;
    if (!drag.active || drag.pointerId !== e.pointerId || !scrollRef.current) return;
    const deltaX = e.clientX - drag.startX;
    const absX = Math.abs(deltaX);

    if (!drag.moved && absX > 4) {
      drag.moved = true;
      setIsDragging(true);
      scrollRef.current.style.cursor = 'grabbing';
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    }
    if (!drag.moved) return;
    e.preventDefault();
    const metrics = getRelatedScrollMetrics();
    if (!metrics) return;
    const { el, maxLeft } = metrics;
    const rawScrollLeft = drag.scrollLeft - deltaX;
    const finalScrollLeft = Math.max(0, Math.min(maxLeft, rawScrollLeft));
    el.scrollLeft = finalScrollLeft;
    const now = performance.now();
    const elapsed = Math.max(1, now - drag.lastTime);
    const currentVelocity = (e.clientX - drag.lastClientX) / elapsed;
    drag.velocity = drag.velocity * 0.35 + currentVelocity * 0.65;
    drag.lastClientX = e.clientX;
    drag.lastTime = now;
  };

  const handleRelatedClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (relatedDragRef.current.suppressClickUntil > Date.now()) {
      e.preventDefault();
      e.stopPropagation();
      relatedDragRef.current.suppressClickUntil = 0;
    }
  };

  const handleRelatedProductClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    productHref: string
  ) => {
    if (relatedDragRef.current.moved || relatedDragRef.current.suppressClickUntil > Date.now()) {
      e.preventDefault();
      return;
    }
    if (isModifiedLinkClick(e)) return;

    e.preventDefault();
    router.push(productHref);
  };

  useEffect(() => {
    return () => stopRelatedDragAnimation();
  }, []);

  useEffect(() => {
    if (!user || !product) {
      setHasPurchased(false);
      return;
    }
    let isMounted = true;
    setCheckingPurchase(true);
    fetch(`/api/products/${product.slug}/check-purchase`)
      .then((res) => res.json())
      .then((data) => {
        if (isMounted && data.hasPurchased) {
          setHasPurchased(true);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (isMounted) setCheckingPurchase(false);
      });
    return () => {
      isMounted = false;
    };
  }, [user, product]);

  useEffect(() => {
    setProduct(initialProduct);
    setReviews(initialReviews);
    setLoading(false);
    setError(null);
  }, [initialProduct, initialReviews]);

  useEffect(() => {
    async function fetchRelatedProducts() {
      if (!product) return;
      setLoadingRelated(true);
      try {
        const res = await fetch(`/api/products/${product.slug}/related`);
        if (res.ok) {
          const data = await res.json();
          // Filter out current product
          const filtered = (data.products || []).filter(
            (p: RelatedProductItem) => p.id !== product.id
          );
          setRelatedProducts(filtered);
        }
      } catch {
        // Silently fail
      } finally {
        setLoadingRelated(false);
      }
    }

    fetchRelatedProducts();
  }, [product?.slug, product?.id]);

  useEffect(() => {
    async function fetchProduct() {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/products/${slug}`);
      const payload = await response.json();

      if (!response.ok) {
        setError('Không thể tải thông tin sản phẩm. Vui lòng thử lại.');
        setLoading(false);
        return;
      }

      const productData = payload.product;
      if (!productData) {
        setError('Sản phẩm không tồn tại.');
        setLoading(false);
        return;
      }

      setProduct(productData);
      setReviews(payload.reviews || []);
      setLoading(false);
    }

    if (!initialProduct || initialProduct.slug !== slug) {
      fetchProduct();
    }
  }, [slug, initialProduct]);

  const availableStock = Math.max(0, Math.floor(Number(product?.stock || 0)));
  const quantityInCart = product
    ? Number(cartItems.find((i) => String(i.id) === String(product.id))?.quantity || 0)
    : 0;
  // Số còn có thể thêm = tồn kho trừ phần đang nằm trong giỏ. Không trừ phần này
  // thì khách thêm nhiều lần vẫn vượt tồn và chỉ nhận lỗi 409 lúc thanh toán.
  const remainingStock = Math.max(0, availableStock - quantityInCart);
  const maxSelectable = Math.max(1, Math.min(99, availableStock || 1));

  // Tồn kho có thể giảm sau khi trang đã mở (fetch lại, hoặc khách khác mua).
  // Kéo số lượng đang chọn về trần mới để nút mua không gửi số vượt tồn.
  useEffect(() => {
    setQuantity((current) => (current > maxSelectable ? maxSelectable : current));
  }, [maxSelectable]);

  /** Trả về '' nếu số lượng hợp lệ, ngược lại là thông báo cho khách. */
  const validateQuantity = () => {
    if (availableStock <= 0) return 'Sản phẩm hiện đang hết hàng.';
    if (quantity > availableStock) {
      return `Chỉ còn ${availableStock} sản phẩm trong kho.`;
    }
    if (quantity > remainingStock) {
      return `Giỏ hàng đã có ${quantityInCart} sản phẩm, chỉ có thể thêm tối đa ${remainingStock} nữa.`;
    }
    return '';
  };

  const handleAddToCart = () => {
    if (!product) return;
    if (authLoading) return;

    const stockError = validateQuantity();
    if (stockError) {
      setCartMessage(stockError);
      setTimeout(() => setCartMessage(''), 3000);
      return;
    }

    if (!user) {
      setCartMessage('Vui lòng đăng nhập để thêm sản phẩm vào giỏ hàng.');
      setTimeout(() => {
        router.push(`/dang-nhap?redirect=${encodeURIComponent(`/san-pham/${slug}`)}`);
      }, 300);
      return;
    }

    const added = addToCart(
      {
        id: product.id,
        name: product.name,
        slug: product.slug,
        price: product.price,
        original_price: product.original_price,
        image: product.image,
        stock: availableStock,
      },
      quantity
    );
    if (!added) return;
    setCartMessage(`Đã thêm ${quantity} sản phẩm vào giỏ hàng!`);
    setTimeout(() => setCartMessage(''), 3000);
  };

  const handleBuyNow = () => {
    if (!product) return;
    if (authLoading) return;

    const stockError = validateQuantity();
    if (stockError) {
      setCartMessage(stockError);
      setTimeout(() => setCartMessage(''), 3000);
      return;
    }

    if (!user) {
      setCartMessage('Vui lòng đăng nhập để mua ngay.');
      setTimeout(() => {
        router.push(`/dang-nhap?redirect=${encodeURIComponent(`/san-pham/${slug}`)}`);
      }, 300);
      return;
    }

    const added = addToCart(
      {
        id: product.id,
        name: product.name,
        slug: product.slug,
        price: product.price,
        original_price: product.original_price,
        image: product.image,
        stock: availableStock,
      },
      quantity
    );

    if (!added) {
      setCartMessage('Không thể thêm sản phẩm để mua ngay, vui lòng thử lại.');
      setTimeout(() => setCartMessage(''), 3000);
      return;
    }

    saveCheckoutSelection([product.id]);
    router.push(`/thanh-toan?buyNow=${encodeURIComponent(product.id)}`);
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product) return;
    setSubmittingReview(true);

    const response = await fetch(`/api/products/${slug}/reviews`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reviewForm),
    });

    setSubmittingReview(false);
    if (response.ok) {
      setReviewSuccess(true);
      setReviewForm({ name: '', email: '', rating: 5, title: '', content: '' });
      const refresh = await fetch(`/api/products/${slug}`);
      const payload = await refresh.json();
      setReviews(payload.reviews || []);
      setProduct(payload.product || product);
      setTimeout(() => setReviewSuccess(false), 4000);
    }
  };

  // Ảnh import cũ còn lưu dạng "//cdn..." nên phải chuẩn hóa trước khi đưa vào
  // next/image, background-image và thẻ <img> thumbnail bên dưới.
  const allImages = product ? normalizeProductImageList([product.image, ...product.images]) : [];
  const activeImage = allImages[selectedImage] || productImageOrFallback(product?.image);

  useEffect(() => {
    setMainImageLoaded(false);
  }, [activeImage]);

  const handleMainImageMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const lensSizePercent = 32;
    const half = lensSizePercent / 2;
    const clampedX = Math.max(half, Math.min(100 - half, x));
    const clampedY = Math.max(half, Math.min(100 - half, y));

    if (zoomFrameRef.current) cancelAnimationFrame(zoomFrameRef.current);
    zoomFrameRef.current = requestAnimationFrame(() => {
      if (zoomLensRef.current) {
        zoomLensRef.current.style.left = `${clampedX}%`;
        zoomLensRef.current.style.top = `${clampedY}%`;
      }
      if (zoomPreviewRef.current) {
        zoomPreviewRef.current.style.backgroundPosition = `${clampedX}% ${clampedY}%`;
      }
      zoomFrameRef.current = null;
    });
  };

  useEffect(
    () => () => {
      if (zoomFrameRef.current) cancelAnimationFrame(zoomFrameRef.current);
    },
    []
  );

  if (loading) {
    return (
      <div className="site-shell min-h-screen flex flex-col">
        <Header />
        <main className="max-w-[1500px] w-full mx-auto px-4 py-8 flex-1">
          <div className="bg-white rounded-lg p-6 animate-pulse">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gray-200 rounded-lg aspect-square" />
              <div className="space-y-4">
                <div className="h-6 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="h-8 bg-gray-200 rounded w-1/3" />
                <div className="h-12 bg-gray-200 rounded" />
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="site-shell min-h-screen flex flex-col">
        <Header />
        <main className="max-w-[1500px] w-full mx-auto px-4 py-8 flex-1">
          <div className="bg-white rounded-lg p-8 text-center">
            <svg
              className="w-16 h-16 text-gray-300 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 className="text-xl font-bold text-gray-700 mb-2">
              {error || 'Sản phẩm không tồn tại'}
            </h2>
            <Link
              href="/"
              className="inline-block mt-4 bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition-colors"
            >
              Về trang chủ
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const specEntries = Object.entries(product.full_specs || {});
  const visibleSpecEntries = specsExpanded ? specEntries : specEntries.slice(0, 14);
  const canToggleSpecs = specEntries.length > 14;
  const safeRating = Number(product.rating || 0);
  const sanitizedDescription = sanitizeProductHtml(product.description || '');
  const plainDescriptionLength = sanitizedDescription
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim().length;
  const canToggleDescription = plainDescriptionLength > 420;
  const isOutOfStock = Number(product.stock || 0) <= 0;

  return (
    <div className="site-shell min-h-screen flex flex-col">
      <Header />
      <main className="max-w-[1500px] w-full mx-auto px-4 py-4 flex-1">
        <nav
          className="flex items-center gap-2 text-sm text-white/85 mb-4"
          data-reveal="true"
          data-load-animate="true"
        >
          <Link href="/" className="text-white hover:text-white">
            Trang chủ
          </Link>
          <span className="text-white/70">/</span>
          <span className="text-white line-clamp-1">{product.name}</span>
        </nav>

        <div
          className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4"
          data-reveal="true"
          data-load-animate="true"
          style={{ '--load-delay': '90ms' } as React.CSSProperties}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">
            <div className="relative">
              <div
                className="relative bg-gray-50 rounded-lg overflow-hidden mb-3 aspect-square flex items-center justify-center"
                onMouseEnter={() => setZoomActive(true)}
                onMouseLeave={() => setZoomActive(false)}
                onMouseMove={handleMainImageMouseMove}
              >
                {product.discount > 0 && (
                  <span className="absolute top-3 left-3 bg-primary text-white text-xs font-bold px-2 py-1 rounded z-10">
                    -{product.discount}%
                  </span>
                )}
                {product.is_best_seller && (
                  <span className="absolute top-3 right-3 bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded z-10">
                    Bán chạy
                  </span>
                )}
                {!mainImageLoaded && (
                  <div className="product-main-image-loader absolute inset-0 z-[1]" aria-hidden />
                )}
                <AppImage
                  key={activeImage}
                  src={activeImage}
                  alt={product.name}
                  fill
                  priority
                  onLoad={() => setMainImageLoaded(true)}
                  onError={() => setMainImageLoaded(true)}
                  className={`product-main-image object-contain p-4 mix-blend-multiply ${
                    mainImageLoaded ? 'product-main-image--ready' : ''
                  }`}
                />
                {zoomActive && (
                  <div
                    ref={zoomLensRef}
                    className="hidden md:block absolute border-2 border-primary/60 bg-white/25 pointer-events-none"
                    style={{
                      width: '32%',
                      height: '32%',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      willChange: 'left, top',
                    }}
                  />
                )}
              </div>
              {zoomActive && (
                <div
                  ref={zoomPreviewRef}
                  className="hidden xl:block absolute z-20 left-[calc(100%+16px)] top-0 w-[320px] h-[320px] rounded-lg border-2 border-gray-300 bg-white shadow-lg"
                  style={{
                    backgroundImage: `url(${activeImage})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '250%',
                    backgroundPosition: '50% 50%',
                    willChange: 'background-position',
                  }}
                />
              )}
              <p className="text-xs text-gray-500 mt-1 hidden md:block">
                Rê chuột vào ảnh để phóng to
              </p>
              {allImages.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {allImages.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden transition-[border-color] duration-150 ${
                        selectedImage === idx
                          ? 'border-primary'
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`Ảnh ${idx + 1}`}
                        className="w-full h-full object-contain p-1 mix-blend-multiply"
                      />
                    </button>
                  ))}
                </div>
              )}

              {sanitizedDescription && (
                <div className="mt-4 border border-gray-100 rounded-lg p-3 md:p-4 bg-white">
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">Mô tả sản phẩm</h3>
                  <div
                    className={`relative overflow-x-auto ${
                      !descriptionExpanded ? 'max-h-[22.75rem] overflow-hidden' : ''
                    }`}
                  >
                    <div
                      className="text-sm text-gray-700 leading-relaxed space-y-2 [&_table]:min-w-full [&_table]:table-auto [&_table]:border-collapse [&_td]:border [&_td]:border-gray-200 [&_td]:p-2 [&_th]:border [&_th]:border-gray-200 [&_th]:p-2 [&_th]:bg-gray-50 [&_img]:max-w-full [&_img]:h-auto"
                      dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
                    />
                    {!descriptionExpanded && canToggleDescription && (
                      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                    )}
                  </div>
                  {canToggleDescription ? (
                    <div className="mt-3 pb-1 flex justify-center">
                      <button
                        type="button"
                        onClick={() => setDescriptionExpanded((prev) => !prev)}
                        className="inline-flex items-center justify-center rounded-full border border-amber-300 bg-gradient-to-b from-amber-50 to-white px-4 py-2 text-sm font-semibold text-amber-700 shadow-[0_6px_16px_rgba(146,64,14,0.14)] transition hover:-translate-y-0.5 hover:from-amber-100 hover:to-amber-50 hover:shadow-[0_10px_22px_rgba(146,64,14,0.2)]"
                      >
                        {descriptionExpanded ? 'Thu gọn' : 'Xem thêm'}
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-800 leading-tight mb-2">
                  {product.name}
                </h1>
                <p className="text-sm text-gray-500 mb-2">
                  Mã sản phẩm:{' '}
                  <span className="font-semibold text-gray-700">
                    {product.sku || 'Đang cập nhật'}
                  </span>
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1">
                    <StarRating rating={safeRating} size="sm" />
                    <span className="text-sm font-semibold text-gray-700">
                      {safeRating.toFixed(1)}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">({product.review_count} đánh giá)</span>
                  {availableStock > 0 ? (
                    <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full inline-block" />
                      {availableStock <= 5 ? `Chỉ còn ${availableStock} sản phẩm` : 'Còn hàng'}
                    </span>
                  ) : (
                    <span className="text-sm text-red-500 font-medium">Hết hàng</span>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-end gap-3 flex-wrap">
                  <span className="text-3xl font-bold text-primary">
                    {formatPrice(product.price)}
                  </span>
                  {product.original_price > product.price && (
                    <span className="text-lg text-gray-400 line-through">
                      {formatPrice(product.original_price)}
                    </span>
                  )}
                  {product.discount > 0 && (
                    <span className="bg-red-100 text-red-600 text-sm font-bold px-2 py-0.5 rounded">
                      Tiết kiệm {formatPrice(product.original_price - product.price)}
                    </span>
                  )}
                </div>
              </div>

              {product.specs && (
                <div className="flex flex-wrap gap-2">
                  {product.specs.split(',').map((spec, i) => (
                    <span
                      key={i}
                      className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded"
                    >
                      {spec.trim()}
                    </span>
                  ))}
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700">Số lượng:</span>
                  <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      aria-label="Giảm số lượng"
                      className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 transition-colors text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8z" />
                      </svg>
                    </button>
                    <span className="w-10 text-center text-sm font-semibold">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(maxSelectable, quantity + 1))}
                      disabled={isOutOfStock || quantity >= maxSelectable}
                      aria-label="Tăng số lượng"
                      className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 transition-colors text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleAddToCart}
                    disabled={authLoading || isOutOfStock}
                    className="flex-1 bg-primary text-white py-3 px-6 rounded-lg font-bold text-base hover:bg-primary-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                    </svg>
                    {isOutOfStock ? 'Đã hết hàng' : user ? 'Thêm vào giỏ hàng' : 'Đăng nhập để mua'}
                  </button>
                  <button
                    onClick={handleBuyNow}
                    disabled={authLoading || isOutOfStock}
                    className="border-2 border-primary text-primary py-3 px-4 rounded-lg font-bold hover:bg-primary hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isOutOfStock ? 'Đã hết hàng' : 'Mua ngay'}
                  </button>
                </div>

                {cartMessage && (
                  <div className="bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-2 rounded-lg flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                    </svg>
                    {cartMessage}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                {[
                  { icon: '🛡️', text: 'Bảo hành chính hãng' },
                  { icon: '🚚', text: 'Giao hàng toàn quốc' },
                  { icon: '↩️', text: 'Đổi trả 30 ngày' },
                  { icon: '💳', text: 'Hỗ trợ trả góp 0%' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                    <span>{item.icon}</span>
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>

              <div className="border border-gray-100 rounded-lg p-3 bg-white">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Thông số kỹ thuật</h3>
                {specEntries.length > 0 ? (
                  <>
                    <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
                      <colgroup>
                        <col className="w-[38%] md:w-[30%]" />
                        <col />
                      </colgroup>
                      <tbody>
                        {visibleSpecEntries.map(([key, value], i) => (
                          <tr key={key} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                            <td className="rounded-l px-3 py-2.5 align-top font-medium text-gray-600 break-words">
                              {key}
                            </td>
                            <td className="rounded-r px-3 py-2.5 align-top text-gray-800 break-words [overflow-wrap:anywhere]">
                              {value}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {canToggleSpecs ? (
                      <div className="mt-3 flex justify-center">
                        <button
                          type="button"
                          onClick={() => setSpecsExpanded((prev) => !prev)}
                          className="inline-flex items-center justify-center rounded-full border border-amber-300 bg-gradient-to-b from-amber-50 to-white px-4 py-2 text-sm font-semibold text-amber-700 shadow-[0_6px_16px_rgba(146,64,14,0.14)] transition hover:-translate-y-0.5 hover:from-amber-100 hover:to-amber-50 hover:shadow-[0_10px_22px_rgba(146,64,14,0.2)]"
                        >
                          {specsExpanded ? 'Thu gọn' : 'Xem thêm'}
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <p className="text-sm text-gray-500">Chưa có thông số kỹ thuật chi tiết.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {relatedProducts.length > 0 && (
          <div
            className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm"
            data-reveal="true"
          >
            <div className="flex flex-col gap-3 border-b border-slate-100/90 bg-white px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5 sm:gap-3">
                <span
                  className="hidden h-8 w-1 shrink-0 rounded-full bg-gradient-to-b from-amber-400 to-orange-500 sm:block"
                  aria-hidden
                />
                <h2 className="text-lg font-black uppercase tracking-tight text-slate-900 sm:text-xl">
                  Sản phẩm liên quan
                </h2>
                <span className="hidden text-xs font-medium text-slate-400 sm:inline-flex sm:items-center sm:gap-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M14 8L10 12L14 16" />
                    <path d="M10 8L14 12L10 16" />
                  </svg>
                  Kéo để xem thêm
                </span>
              </div>
            </div>

            <div className="bg-[#f8f9fa] p-4 md:p-5 relative">
              {/* Gradient fade indicators */}
              <div className="pointer-events-none absolute left-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-r from-[#f8f9fa] to-transparent md:hidden" />
              <div className="pointer-events-none absolute right-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-l from-[#f8f9fa] to-transparent md:hidden" />

              {/* Desktop gradient overlays */}
              <div className="pointer-events-none absolute left-12 top-0 bottom-0 z-10 hidden w-12 bg-gradient-to-r from-[#f8f9fa] to-transparent md:block" />
              <div className="pointer-events-none absolute right-12 top-0 bottom-0 z-10 hidden w-12 bg-gradient-to-l from-[#f8f9fa] to-transparent md:block" />

              <div className="flex items-stretch gap-1.5 sm:gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const el = document.getElementById('related-products-scroll');
                    if (el) el.scrollBy({ left: -(el.clientWidth * 0.8), behavior: 'smooth' });
                  }}
                  className="hidden h-10 w-10 shrink-0 items-center justify-center self-center rounded-full border border-slate-200/80 bg-white/95 text-slate-700 shadow-sm transition hover:bg-white hover:shadow-md md:flex z-20"
                  aria-label="Xem sản phẩm trước"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                    aria-hidden
                  >
                    <path
                      fillRule="evenodd"
                      d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
                    />
                  </svg>
                </button>
                <div
                  id="related-products-scroll"
                  ref={scrollRef}
                  onPointerDown={handleRelatedPointerDown}
                  onPointerUp={handleRelatedPointerEnd}
                  onPointerCancel={handleRelatedPointerEnd}
                  onPointerMove={handleRelatedPointerMove}
                  onDragStart={(e) => e.preventDefault()}
                  onDrop={(e) => e.preventDefault()}
                  onClickCapture={handleRelatedClickCapture}
                  className={`min-w-0 flex-1 overflow-x-auto overflow-y-hidden overscroll-contain px-1 py-1 scrollbar-hide select-none [-webkit-overflow-scrolling:touch] [touch-action:pan-y] ${
                    isDragging ? 'snap-none cursor-grabbing' : 'snap-x snap-proximity cursor-grab'
                  }`}
                  style={
                    {
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none',
                      touchAction: 'pan-y',
                      overscrollBehavior: 'contain',
                      WebkitUserDrag: 'none',
                    } as React.CSSProperties
                  }
                >
                  <div className="flex min-h-0 gap-3 pr-2 md:gap-4 md:pr-2">
                    {relatedProducts.map((item, idx) => {
                      const productHref = `/san-pham/${item.slug}`;
                      const relatedOutOfStock = Number(item.stock || 0) <= 0;
                      const specsList = buildProductCardSpecBadges(
                        item.specs,
                        item.full_specs,
                        item.name
                      );
                      const pct = relatedDiscountPercent(item);
                      return (
                        <div
                          key={item.id}
                          data-related-card="1"
                          data-reveal="true"
                          data-reveal-delay={`${Math.min(idx, 5) * 45}ms`}
                          className="group relative flex min-h-[552px] min-w-[176px] w-[calc((100%-0.75rem)/2)] shrink-0 snap-start flex-col rounded-none border border-slate-200 bg-white p-3 transition-colors duration-200 hover:border-slate-300 sm:w-[calc((100%-1.5rem)/3)] md:w-[calc((100%-2.25rem)/4)] lg:w-[calc((100%-4rem)/5)]"
                          draggable={false}
                          onDragStart={(e) => e.preventDefault()}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (authLoading) return;
                              if (relatedOutOfStock) {
                                setCartMessage('Sản phẩm hiện đang hết hàng.');
                                setTimeout(() => setCartMessage(''), 2500);
                                return;
                              }
                              if (!user) {
                                setCartMessage('Vui lòng đăng nhập để thêm sản phẩm vào giỏ hàng.');
                                setTimeout(() => {
                                  router.push(
                                    `/dang-nhap?redirect=${encodeURIComponent(productHref)}`
                                  );
                                }, 300);
                                return;
                              }
                              const added = addToCart({
                                id: item.id,
                                name: item.name,
                                slug: item.slug,
                                price: item.price,
                                original_price: item.original_price,
                                image: item.image,
                              });
                              if (added) {
                                setCartMessage('Đã thêm sản phẩm vào giỏ hàng.');
                                setTimeout(() => setCartMessage(''), 2500);
                              }
                            }}
                            disabled={authLoading || relatedOutOfStock}
                            className="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center bg-transparent p-0 text-black transition hover:scale-105 hover:text-slate-800 disabled:scale-100 disabled:cursor-not-allowed disabled:text-slate-300"
                            aria-label={
                              relatedOutOfStock
                                ? 'Sản phẩm đã hết hàng'
                                : 'Thêm sản phẩm vào giỏ hàng'
                            }
                            title={relatedOutOfStock ? 'Đã hết hàng' : 'Thêm vào giỏ hàng'}
                          >
                            <RelatedCartIcon />
                          </button>
                          <Link
                            href={productHref}
                            draggable={false}
                            onDragStart={(e) => e.preventDefault()}
                            onClickCapture={(e) => {
                              if (
                                relatedDragRef.current.moved ||
                                relatedDragRef.current.suppressClickUntil > Date.now()
                              ) {
                                e.preventDefault();
                                e.stopPropagation();
                              }
                            }}
                            onClick={(e) => handleRelatedProductClick(e, productHref)}
                            className="group/spec-trigger flex flex-col flex-1"
                          >
                            <div className="relative mb-3 aspect-[1.18/1] overflow-visible bg-white pt-5">
                              <AppImage
                                src={productImageOrFallback(item.image)}
                                alt={item.name}
                                fill
                                sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, 25vw"
                                className="pointer-events-none select-none object-contain p-3 mix-blend-multiply"
                              />
                            </div>
                            <h3 className="mb-2 min-h-[7.1rem] break-words text-left text-[14px] font-bold leading-[1.45] text-slate-800">
                              {item.name}
                            </h3>
                            <div className="mb-3 bg-[#ececec] px-2.5 py-2 text-[12px] text-slate-600">
                              {specsList.length ? (
                                <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                                  {specsList.map((spec, specIndex) => (
                                    <div
                                      key={`${spec}-${specIndex}`}
                                      className="flex min-w-0 items-center gap-1.5"
                                    >
                                      <RelatedSpecIcon index={specIndex} />
                                      <span className="truncate leading-snug">{spec}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="line-clamp-1">Thông số đang cập nhật</div>
                              )}
                            </div>
                            <ProductSpecsHoverCard
                              name={item.name}
                              price={item.price}
                              originalPrice={item.original_price}
                              stock={item.stock}
                              fullSpecs={item.full_specs}
                              specsText={item.specs}
                            />
                            <div className="mt-auto text-left">
                              {Number(item.original_price || 0) > Number(item.price || 0) ? (
                                <div className="mb-0.5 text-[13px] text-slate-500 line-through">
                                  {formatPrice(item.original_price)}
                                </div>
                              ) : null}
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-[18px] font-black leading-none text-red-600">
                                  {formatPrice(item.price)}
                                </span>
                                {pct > 0 ? (
                                  <span className="border border-red-500 px-1.5 py-0.5 text-[12px] font-medium leading-none text-red-600">
                                    -{pct}%
                                  </span>
                                ) : null}
                              </div>
                              <div className="mt-2 text-[13px] font-semibold text-orange-500">
                                {Number(item.rating || 0).toFixed(1)} ★{' '}
                                <span className="font-normal text-slate-500">
                                  ({Number(item.review_count || 0)} đánh giá)
                                </span>
                              </div>
                            </div>
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const el = document.getElementById('related-products-scroll');
                    if (el) el.scrollBy({ left: el.clientWidth * 0.8, behavior: 'smooth' });
                  }}
                  className="hidden h-10 w-10 shrink-0 items-center justify-center self-center rounded-full border border-slate-200/80 bg-white/95 text-slate-700 shadow-sm transition hover:bg-white hover:shadow-md md:flex z-20"
                  aria-label="Xem sản phẩm sau"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                    aria-hidden
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {showroomVisible ? (
          <div data-reveal="true">
            <ShowroomSection
              title={showroomTitle || 'HỆ THỐNG SHOWROOM DN TECH'}
              items={showroomItems}
            />
          </div>
        ) : null}

        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-4" data-reveal="true">
          <div className="px-6 py-3 text-sm font-semibold border-b border-gray-200 text-primary flex items-center gap-2">
            Đánh giá
            {reviews.length > 0 && (
              <span className="bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {reviews.length}
              </span>
            )}
          </div>

          <div className="p-4 md:p-6">
            <div className="space-y-6">
              <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary">{safeRating.toFixed(1)}</div>
                  <StarRating rating={safeRating} size="lg" />
                  <div className="text-xs text-gray-500 mt-1">{product.review_count} đánh giá</div>
                </div>
                <div className="flex-1 space-y-1">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const count = reviews.filter((r) => r.rating === star).length;
                    const pct = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                    return (
                      <div key={star} className="flex items-center gap-2 text-xs">
                        <span className="w-4 text-gray-600">{star}</span>
                        <svg
                          className="w-3 h-3 text-yellow-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-yellow-400 h-2 rounded-full transition-[width] duration-200"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="w-6 text-gray-500">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {reviews.length > 0 ? (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.id} className="border border-gray-100 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-800 text-sm">
                              {review.reviewer_name}
                            </span>
                            {review.is_verified_purchase && (
                              <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">
                                ✓ Đã mua hàng
                              </span>
                            )}
                          </div>
                          <StarRating rating={review.rating} size="sm" />
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(review.created_at).toLocaleDateString('vi-VN')}
                        </span>
                      </div>
                      {review.title && (
                        <p className="font-semibold text-gray-700 text-sm mb-1">{review.title}</p>
                      )}
                      <p className="text-sm text-gray-600 leading-relaxed">{review.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">
                  Chưa có đánh giá nào. Hãy là người đầu tiên đánh giá!
                </p>
              )}

              <div className="border-t border-gray-200 pt-6">
                <h3 className="font-bold text-gray-800 mb-4">Viết đánh giá của bạn</h3>
                {authLoading || checkingPurchase ? (
                  <div className="text-sm text-gray-500 py-4 text-center">Đang tải...</div>
                ) : !user ? (
                  <div className="bg-gray-50 border border-gray-200 text-center py-8 rounded-lg mb-4">
                    <p className="text-gray-600 mb-3 text-sm">
                      Vui lòng đăng nhập để đánh giá sản phẩm này.
                    </p>
                    <Link
                      href="/dang-nhap"
                      rel="nofollow"
                      onClick={(event) => {
                        event.preventDefault();
                        router.push(
                          `/dang-nhap?redirect=${encodeURIComponent(`/san-pham/${slug}`)}`
                        );
                      }}
                      className="inline-block bg-primary text-white font-bold px-6 py-2 rounded-lg hover:bg-primary-dark transition text-sm"
                    >
                      Đăng nhập
                    </Link>
                  </div>
                ) : !hasPurchased ? (
                  <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm px-4 py-3 rounded-lg mb-4">
                    Bạn chỉ có thể đánh giá sau khi đã mua sản phẩm này.
                  </div>
                ) : (
                  <>
                    {reviewSuccess && (
                      <div className="bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-lg mb-4">
                        Cảm ơn bạn đã đánh giá sản phẩm!
                      </div>
                    )}
                    <form onSubmit={handleSubmitReview} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Họ tên *
                          </label>
                          <input
                            type="text"
                            required
                            value={reviewForm.name}
                            onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary"
                            placeholder="Nguyễn Văn A"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email *
                          </label>
                          <input
                            type="email"
                            required
                            value={reviewForm.email}
                            onChange={(e) =>
                              setReviewForm({ ...reviewForm, email: e.target.value })
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary"
                            placeholder="email@example.com"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Đánh giá *
                        </label>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                              className={`w-8 h-8 transition-colors ${star <= reviewForm.rating ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-300'}`}
                            >
                              <svg fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            </button>
                          ))}
                          <span className="text-sm text-gray-500 ml-2">
                            {reviewForm.rating}/5 sao
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tiêu đề đánh giá
                        </label>
                        <input
                          type="text"
                          value={reviewForm.title}
                          onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary"
                          placeholder="Tóm tắt đánh giá của bạn"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nội dung đánh giá *
                        </label>
                        <textarea
                          required
                          rows={4}
                          value={reviewForm.content}
                          onChange={(e) =>
                            setReviewForm({ ...reviewForm, content: e.target.value })
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary resize-none"
                          placeholder="Chia sẻ trải nghiệm của bạn về sản phẩm này..."
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={submittingReview}
                        className="bg-primary text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {submittingReview ? 'Đang gửi...' : 'Gửi đánh giá'}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
